/**
 * The item browser. F-82, and the point of the whole console.
 *
 * **A count of 538 items says nothing about whether they are worth answering.** This
 * is the screen where that gets decided, so every decision in it is about making 538
 * items judgeable in one sitting rather than about looking tidy:
 *
 * - **Everything is loaded, then filtered here.** The endpoint pages; this follows the
 *   cursor to the end before it draws (see `usePages`). Filtering a hundred-item slice
 *   and calling it "sorted by lapses" would rank inside an arbitrary window.
 * - **Prompt, answer, rubric and sources are all on the row, unelided.** Judging an
 *   item means reading the answer against the prompt. A list that hides the answer
 *   behind a click is 538 clicks, which is the tedium F-82 exists to remove.
 * - **The FSRS state sits beside each item, not behind a tab.** "This item has six
 *   lapses" is the strongest available evidence that an item is badly written.
 * - **Sorting is by evidence, not by id.** Lapses first, difficulty first, or by due
 *   date. The badly-written items are at one end of those orders.
 * - **Heuristic flags surface the ones most likely to be wrong** — a recall item with
 *   no answer, no rubric or no source, and duplicated prompts. They are a shortlist,
 *   never a verdict; `flagsOf` says so.
 * - **`j`/`k` walks the list.** Reading 538 items with a trackpad is the tedium in a
 *   different costume.
 *
 * English-only, no `t()`. See the note at the top of `Console.tsx`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  getObjective,
  isError,
  listItems,
  nextCursorOf,
  type AdminItem,
  type AdminNode,
  type AdminObjectiveDetail,
} from './api';
import { Failed, Id, Nil, ago, fixed, isOverdue, localTitle, stamp, usePages } from './parts';

type Sort = 'order' | 'due' | 'lapses' | 'difficulty' | 'stability' | 'flags';

const SORTS: readonly { value: Sort; label: string }[] = [
  { value: 'order', label: 'curriculum order' },
  { value: 'flags', label: 'flagged first' },
  { value: 'lapses', label: 'most lapses' },
  { value: 'difficulty', label: 'hardest' },
  { value: 'stability', label: 'weakest memory' },
  { value: 'due', label: 'due soonest' },
];

const STATES = ['new', 'learning', 'review', 'relearning'] as const;

/**
 * The cheap tests for "this item is probably not worth answering".
 *
 * **A shortlist, not a verdict.** None of these can tell a bad question from a good
 * one — that needs a person reading it, which is what the rest of the screen is for.
 * What they can do is put the items most likely to be wrong in front of that person
 * first: a recall item with no answer cannot be graded at all, one with no cited
 * source cannot be checked against the research it came from, and two items with the
 * same prompt mean the learner will be asked the same thing twice and the generator
 * wasted a request.
 *
 * Deliberately not a score. A number would invite treating 0.4 as worse than 0.5,
 * which none of this supports.
 */
function flagsOf(item: AdminItem, promptCounts: Map<string, number>): string[] {
  const flags: string[] = [];
  if (item.kind === 'recall' && !item.answer?.trim()) flags.push('no answer');
  if (item.kind === 'recall' && !item.rubric?.trim()) flags.push('no rubric');
  if (sourcesOf(item).length === 0) flags.push('no source');
  if ((promptCounts.get(normalise(item.prompt)) ?? 0) > 1) flags.push('duplicate prompt');
  // Twenty characters is not a real threshold, it is a floor: a prompt this short is
  // either a fragment or a stub, and both are generation failures worth seeing.
  if (normalise(item.prompt).length < 20) flags.push('very short prompt');
  return flags;
}

/*
 * Every read of an admin field tolerates it being absent, here and below.
 *
 * Not defensive habit: the admin API is being written alongside this screen, and a
 * missing `sourceIds` or a null `prompt` must render as "none cited" on one row
 * rather than blanking the list — the operator is looking at this screen *because*
 * something upstream is not what it should be.
 */
const normalise = (text: string | null | undefined) =>
  (text ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

const sourcesOf = (item: AdminItem): string[] =>
  Array.isArray(item.sourceIds) ? item.sourceIds : [];

/** The stored title, in whichever language it exists. Nothing here translates anything. */
const nodeTitle = (node: AdminNode | undefined) =>
  node ? localTitle(node.title, node.nodeId) : undefined;

/** One item, whole: what gets asked, what counts as right, and how it is going. */
function Row({
  item,
  index,
  node,
  flags,
  selected,
  compact,
  onSelect,
  rowRef,
}: {
  item: AdminItem;
  index: number;
  node: AdminNode | undefined;
  flags: string[];
  selected: boolean;
  compact: boolean;
  onSelect: () => void;
  rowRef: (element: HTMLLIElement | null) => void;
}) {
  const review = item.review;
  const overdue = isOverdue(review?.due);

  return (
    <li
      ref={rowRef}
      className={[
        'item',
        selected ? 'is-selected' : '',
        flags.length > 0 ? 'is-flagged' : '',
        compact ? 'is-compact' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onSelect}
    >
      <div className="item-body">
        <p className="item-meta">
          <span className="item-index">#{index + 1}</span>
          <span className={`tag kind-${item.kind}`}>{item.kind}</span>
          <span className="item-node">{nodeTitle(node) ?? item.nodeId}</span>
          <Id value={item.itemId} />
          {flags.map((flag) => (
            <span key={flag} className="tag flag">
              {flag}
            </span>
          ))}
        </p>

        <p className="item-prompt">{item.prompt || '(no prompt stored)'}</p>

        {item.answer ? (
          <p className="item-answer">
            <span className="item-label">answer</span>
            {item.answer}
          </p>
        ) : (
          item.kind === 'recall' && (
            <p className="item-answer bad">
              <span className="item-label">answer</span>
              missing — this item cannot be graded
            </p>
          )
        )}

        {!compact && item.rubric && (
          <p className="item-rubric">
            <span className="item-label">rubric</span>
            {item.rubric}
          </p>
        )}

        {!compact && (
          <p className="item-sources">
            <span className="item-label">sources</span>
            {sourcesOf(item).length === 0 ? (
              <Nil what="none cited" />
            ) : (
              sourcesOf(item).map((sourceId) => <Id key={sourceId} value={sourceId} />)
            )}
          </p>
        )}
      </div>

      {/*
        The review state, in a fixed column so the numbers line up down the list.
        Aligned deliberately: comparing stability across items is a glance if the
        figures are in the same place on every row and a chore if they are not.
      */}
      <div className="item-review">
        {review ? (
          <>
            <span className={`tag state-${review.state}`}>{review.state ?? 'unknown'}</span>
            <dl className="fsrs">
              <div className={overdue ? 'bad' : undefined}>
                <dt>due</dt>
                <dd title={review.due}>{ago(review.due)}</dd>
              </div>
              <div>
                <dt>stability</dt>
                <dd>{fixed(review.stability)}d</dd>
              </div>
              <div>
                <dt>difficulty</dt>
                <dd>{fixed(review.difficulty)}</dd>
              </div>
              <div>
                <dt>reps</dt>
                <dd>{review.reps ?? '—'}</dd>
              </div>
              <div className={(review.lapses ?? 0) > 0 ? 'warn' : undefined}>
                <dt>lapses</dt>
                <dd>{review.lapses ?? '—'}</dd>
              </div>
              <div>
                <dt>sent</dt>
                <dd title={review.sentAt ?? undefined}>
                  {review.sentAt ? ago(review.sentAt) : 'never'}
                </dd>
              </div>
            </dl>
          </>
        ) : (
          /* No review row at all, which is not the same as `state: 'new'`: it means
             the objective was never started, so this item has no memory attached to
             anybody. Said in words, because an empty column reads as a bug. */
          <span className="sub">not scheduled</span>
        )}
      </div>
    </li>
  );
}

export default function Items() {
  const { objectiveId } = useParams<{ objectiveId: string }>();
  const [params, setParams] = useSearchParams();

  /*
   * The filters live in the URL, not in component state.
   *
   * So that "the items for this node" is a link — which is what the curriculum tree
   * and the learner's fault verdict both hand over — and so that a finding can be
   * pasted into a message rather than described as a sequence of clicks.
   */
  const node = params.get('node') ?? '';
  const sort = (params.get('sort') as Sort | null) ?? 'order';
  const query = params.get('q') ?? '';
  const state = params.get('state') ?? '';
  const flaggedOnly = params.get('flagged') === '1';

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const [detail, setDetail] = useState<AdminObjectiveDetail>();
  const [compact, setCompact] = useState(false);
  const [selected, setSelected] = useState(0);
  const rows = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    if (!objectiveId) return;
    let stale = false;
    void getObjective(objectiveId).then((result) => {
      if (!stale && !isError(result)) setDetail(result);
    });
    return () => {
      stale = true;
    };
  }, [objectiveId]);

  /*
   * The node filter is applied by the API, not here, even though everything else on
   * this screen filters locally. `nodeId` is the one filter the endpoint takes, and
   * one node's items is a much smaller download than the whole objective's — which
   * matters on the click-through from a 69-node curriculum tree.
   */
  const fetchPage = useCallback(
    async (cursor?: string) => {
      if (!objectiveId) return { rows: [], next: undefined };
      const result = await listItems(objectiveId, node || undefined, cursor);
      if (isError(result)) return result;
      return { rows: result.items, next: nextCursorOf(result) };
    },
    [objectiveId, node],
  );

  const { rows: items, error, loading, done, pages, loadAll } = usePages(fetchPage, { eager: true });

  const nodes = detail?.nodes ?? [];
  const byId = useMemo(() => new Map(nodes.map((n) => [n.nodeId, n])), [nodes]);
  /** Curriculum position, so the default order matches the plan rather than an id. */
  const positionOf = useMemo(
    () => new Map(nodes.map((n, index) => [n.nodeId, index])),
    [nodes],
  );

  const promptCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      const key = normalise(item.prompt);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [items]);

  const flagged = useMemo(
    () => new Map(items.map((item) => [item.itemId, flagsOf(item, promptCounts)])),
    [items, promptCounts],
  );

  const shown = useMemo(() => {
    const needle = normalise(query);
    const filtered = items.filter((item) => {
      const flags = flagged.get(item.itemId) ?? [];
      if (flaggedOnly && flags.length === 0) return false;
      if (state === 'unscheduled' && item.review) return false;
      if (state === 'overdue' && !isOverdue(item.review?.due)) return false;
      if (STATES.some((s) => s === state) && item.review?.state !== state) return false;
      if (!needle) return true;
      // Searching the rubric and the itemId too, not just the prompt: "which item was
      // that" is asked from a screenshot of the thread as often as from a word in the text.
      return (
        normalise(`${item.prompt} ${item.answer ?? ''} ${item.rubric ?? ''}`).includes(needle) ||
        item.itemId.includes(query.trim())
      );
    });

    const order = [...filtered];
    const rank = (item: AdminItem) => positionOf.get(item.nodeId) ?? Number.MAX_SAFE_INTEGER;
    switch (sort) {
      case 'due':
        // Items with no review state have no due date and sort last rather than
        // first: an unscheduled item is not urgent, it is not scheduled.
        order.sort(
          (a, b) =>
            (a.review ? Date.parse(a.review.due) : Infinity) -
            (b.review ? Date.parse(b.review.due) : Infinity),
        );
        break;
      case 'lapses':
        order.sort((a, b) => (b.review?.lapses ?? -1) - (a.review?.lapses ?? -1));
        break;
      case 'difficulty':
        order.sort((a, b) => (b.review?.difficulty ?? -1) - (a.review?.difficulty ?? -1));
        break;
      case 'stability':
        order.sort((a, b) => (a.review?.stability ?? Infinity) - (b.review?.stability ?? Infinity));
        break;
      case 'flags':
        order.sort(
          (a, b) =>
            (flagged.get(b.itemId)?.length ?? 0) - (flagged.get(a.itemId)?.length ?? 0) ||
            rank(a) - rank(b),
        );
        break;
      default:
        order.sort((a, b) => rank(a) - rank(b));
    }
    return order;
  }, [items, flagged, flaggedOnly, query, state, sort, positionOf]);

  // Whatever was selected before the filter changed is very likely gone. Clamping
  // rather than resetting keeps the position when the list only shrank a little.
  useEffect(() => {
    setSelected((current) => Math.min(current, Math.max(0, shown.length - 1)));
  }, [shown.length]);

  /*
   * `j` and `k`, because this list is read one item at a time, in order, 538 times.
   *
   * Scoped to keypresses that are not going into a field — without that check,
   * typing "job" into the search box would walk the list instead of filtering it,
   * which is the bug this guard exists for.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const move = (delta: number) => {
        event.preventDefault();
        setSelected((current) => {
          const next = Math.min(Math.max(current + delta, 0), Math.max(0, shown.length - 1));
          rows.current[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      };

      if (event.key === 'j' || event.key === 'ArrowDown') move(1);
      else if (event.key === 'k' || event.key === 'ArrowUp') move(-1);
      else if (event.key === 'c') setCompact((value) => !value);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shown.length]);

  if (!objectiveId) return null;

  const flaggedCount = [...flagged.values()].filter((list) => list.length > 0).length;

  return (
    <>
      <nav className="crumbs">
        <Link className="admin-link" to="/admin">
          learners
        </Link>
        <span aria-hidden="true">/</span>
        <Link className="admin-link" to={`/admin/objective/${objectiveId}`}>
          {detail ? localTitle(detail.objective.title, objectiveId) : objectiveId}
        </Link>
        <span aria-hidden="true">/</span>
        <span>items</span>
      </nav>

      <section className="admin-panel">
        <header className="admin-head">
          <h1>Items</h1>
          <p className="sub">
            {items.length} loaded in {pages} page{pages === 1 ? '' : 's'}
            {done ? '' : ', more to come'} · {shown.length} shown · {flaggedCount} flagged
            {loading && ' · loading…'}
          </p>
        </header>

        {error && <Failed error={error} what="Items" />}

        <div className="filters">
          <label className="filter">
            <span>node</span>
            <select value={node} onChange={(event) => setParam('node', event.target.value)}>
              <option value="">every node</option>
              {nodes.map((n) => (
                <option key={n.nodeId} value={n.nodeId}>
                  {nodeTitle(n)}
                  {n.active === false ? ' (off)' : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="filter">
            <span>search</span>
            <input
              type="search"
              value={query}
              placeholder="prompt, answer, rubric, itemId"
              onChange={(event) => setParam('q', event.target.value)}
            />
          </label>

          <label className="filter">
            <span>review state</span>
            <select value={state} onChange={(event) => setParam('state', event.target.value)}>
              <option value="">any</option>
              {STATES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
              <option value="overdue">overdue</option>
              <option value="unscheduled">not scheduled</option>
            </select>
          </label>

          <label className="filter">
            <span>sort</span>
            <select value={sort} onChange={(event) => setParam('sort', event.target.value)}>
              {SORTS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="filter inline">
            <input
              type="checkbox"
              checked={flaggedOnly}
              onChange={(event) => setParam('flagged', event.target.checked ? '1' : '')}
            />
            <span>flagged only</span>
          </label>

          <label className="filter inline">
            <input
              type="checkbox"
              checked={compact}
              onChange={(event) => setCompact(event.target.checked)}
            />
            <span>compact</span>
          </label>

          {!done && !loading && (
            <button type="button" className="admin-button quiet" onClick={loadAll}>
              Load the rest
            </button>
          )}
        </div>

        <p className="sub keys">
          <kbd>j</kbd> / <kbd>k</kbd> next and previous · <kbd>c</kbd> compact · flags are a
          shortlist, not a verdict
        </p>
      </section>

      {shown.length === 0 && !loading ? (
        <p className="admin-empty">
          {items.length === 0
            ? 'No items. Either generation has not run for this objective, or the endpoint is not deployed.'
            : 'Nothing matches these filters.'}
        </p>
      ) : (
        <ol className="items">
          {shown.map((item, index) => (
            <Row
              key={item.itemId}
              item={item}
              index={index}
              node={byId.get(item.nodeId)}
              flags={flagged.get(item.itemId) ?? []}
              selected={index === selected}
              compact={compact}
              onSelect={() => setSelected(index)}
              rowRef={(element) => {
                rows.current[index] = element;
              }}
            />
          ))}
        </ol>
      )}

      {detail?.objective.itemVersion && (
        <p className="sub">
          itemVersion <Id value={detail.objective.itemVersion} /> · objective created{' '}
          {stamp(detail.objective.createdAt)}
        </p>
      )}
    </>
  );
}
